import {
  API_BASE_URL,
  API_ENDPOINTS,
  API_PUBLIC_AUTH_TOKEN,
} from "../constants/api";
import { RCODES } from "../constants/rcodes";
import Http from "../services/http";
import { DEFAULT_COUNTRY_CODE } from "../constants/locale";
import { DETECTED, SITE_SETTINGS } from "../constants/site-settings";
import { useDispatch, useSelector } from "react-redux";
import {
  actionSetSysProps,
  actionSetSysPropsLoading,
} from "../store/actions/site-actions";
import { ApplicationState } from "../store";
import { SystemState } from "../store/reducers/site-reducer";
import {
  DISABLED_FEATURES,
  useFeatureToggle,
} from "../utils/feature-toggle/useFeatureToggle";

export const useSiteSettingsHook = () => {
  const dispatch = useDispatch();
  const siteSettings = useSelector<ApplicationState, SystemState>(
    (state) => state.system
  );
  const { isDisabled } = useFeatureToggle();

  const loadSystemProperties = async () => {
    const url = `${API_BASE_URL}${API_ENDPOINTS.SYSTEM_PROPERTIES}`;
    const extraHeaders = {
      Authorization: API_PUBLIC_AUTH_TOKEN,
    };
    dispatch(actionSetSysPropsLoading(true));
    try {
      const res = await Http.get(url, extraHeaders);
      if (res.rcode !== RCODES.SUCCESS) {
        throw new Error("Error in fetching system properties");
      }
      //Transform the response into flat object
      // e.g {"rcode": 0, "rmsg": "Success", "system_properties": [ {"version": 32,"property_key": "SA_AGE", "property_value": "DETECTED" }]...} to {SA_AGE: "DETECTED", ...},
      const systemProperties = res.system_properties.reduce(
        (acc: any, curr: any) => {
          const key = curr.property_key;
          const value = curr.property_value;
          acc[key] = value;
          return acc;
        },
        {}
      );
      //Save to session storage, convert the response into json-formatted string
      const serializedSystemProperties = JSON.stringify(systemProperties);
      sessionStorage.setItem(SITE_SETTINGS, serializedSystemProperties);
      dispatch(actionSetSysProps(systemProperties));
      // Save system properties to sessionStorage
      sessionStorage.setItem("SITE_SETTINGS", JSON.stringify(systemProperties));
      dispatch(actionSetSysPropsLoading(false));
    } catch (e) {
      console.error(e);
      dispatch(actionSetSysPropsLoading(false));
    }
  };

  /**
   * API call for getting geoip of user.
   * @private
   * @returns geoip
   */
  const _getDefaultGeoip = async (): Promise<string> => {
    try {
      const url = `${API_BASE_URL}${API_ENDPOINTS.GEOIP}`;
      const res = await fetch(url, {
        method: "GET",
        headers: { Authorization: API_PUBLIC_AUTH_TOKEN },
      });

      const { headers } = res;
      return headers.get("country") || DEFAULT_COUNTRY_CODE;
    } catch (error) {
      console.error(error);
      return DEFAULT_COUNTRY_CODE;
    }
  };

  const getCountryCode = async (): Promise<string> => {
    let countryCode = DEFAULT_COUNTRY_CODE;

    // Only use sysProps.GEO_IP if it's set and not "DETECTED"
    // If GEO_IP is empty (due to API error), fall through to _getDefaultGeoip()
    if (
      siteSettings.sysProps.GEO_IP !== DETECTED &&
      !isDisabled(DISABLED_FEATURES.SITE_SETTINGS)
    ) {
      countryCode = siteSettings.sysProps.GEO_IP;
    } else {
      countryCode = await _getDefaultGeoip();
    }

    return countryCode;
  };

  return {
    loadSystemProperties,
    getCountryCode,
  };
};
